import { cn } from '@neo/lib/utils'
import { Tooltip } from '@neo/primitives/tooltip'
import { Lock } from 'lucide-react'
import { StudioBadge } from 'v2/shared/onboarding/studio-badge'
import type { BuildChoice, BuildOption, Framework, FrameworkOption } from './wizard-data'

// Text scale across the wizard: option TITLES are 14px (p-600); everything
// else is the 13px `s` scale, above which only the step question sits (h3-600,
// 18px).
//
// Inside the grey follow-up panel, labels use neo's Label style —
// `text-secondary text-s-500` — because that is literally what neo's Input and
// Checkbox render for "Answer URL" and "Public URI" in the applications form,
// which is the panel this one is modelled on. Descriptions stay text-s-400.
//
// All product tags use the same quiet grey pill. The semantic tones still tell
// us whether a label names a product or a restriction; the lock is the visible
// distinction for Enterprise. Keeping AI Agent Studio in this family prevents
// one option from looking selected before the user has made a choice.
//
// 20px tall, both tones. The height comes out of the
// vertical padding rather than the line box: dropping py-0.5 leaves the 1px edge
// (the gradient's p-px, the enterprise border) plus text-xs-500's own 18px line,
// which lands on exactly 20px — the same height as the p-600 title beside it, so
// a badge no longer sets the title row's height at all. text-xs-500 is the
// scale's 12px/500, one step down from the 13px the pills and body copy use;
// going further (xxs, 10px) drops below the smallest size this app sets anywhere.
//
// No top margin on either tone: the badge sits INLINE with the title, and the
// title row's items-center does the aligning (the margin was there to nudge it
// level with the title back when it was a right-anchored sibling of the whole
// text column).
const OptionBadge = ({ label, tone, tooltip }: NonNullable<BuildOption['badge']>) => {
  if (tone === 'studio') return <StudioBadge label={label} />
  // Every tone shares the quiet grey pill; the lock is the restriction marker,
  // so only 'enterprise' carries it.
  const pill = (
    // Deliberately quiet — white with a hairline border — so the badge does not
    // compete with the title it qualifies; a filled, darker pill reads as the
    // loudest thing on the row.
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-st2 bg-background px-2 font-jetbrains text-muted-foreground text-label uppercase">
      {/* 10px lock, down from 12px: at the smaller text size a 12px glyph was the
          heaviest thing in the pill. */}
      {tone === 'enterprise' && <Lock className="h-2.5 w-2.5" strokeWidth={2} />}
      {label}
    </span>
  )
  // The badge sits inside the option's own <button> (BuildCard), so the trigger
  // must stay a span — hover/focus still reach it through the parent. No local
  // TooltipProvider: /onboarding is a VISUAL takeover (fixed inset-0) but still
  // renders as a child of MainLayout, which already owns the provider.
  return tooltip ? <Tooltip content={tooltip}>{pill}</Tooltip> : pill
}

// Radio glyph, styled to MATCH neo's RadioGroupItem: 16px,
// a 1px primary border in BOTH states, and a filled inner dot as the checked
// indicator — the exact treatment primitives/base/radio-group.tsx paints, so
// this reads as the same control family as every real radio in the app. It
// cannot BE that primitive: Radix's item renders a <button>, these glyphs sit
// inside buttons already (nesting interactive controls is invalid), and the
// pills' click-to-clear semantics are not radio semantics. main's wizard
// shipped a divergent look here (grey border unchecked, solid disc checked);
// this aligns it.
//
// self-center, not the mt-0.5 the badge uses: BuildCard puts it beside a two-line
// title-and-description column, and a control pinned to the first line reads as
// belonging to the title rather than to the whole option. Centering is against
// the flex line, which the text column sets, so it holds whether or not the
// description wraps.
const Radio = ({ selected }: { selected: boolean }) => (
  <span
    // border-primary in both states, exactly like RadioGroupItem — the unchecked
    // ring is part of the design system's radio identity, not a selection cue.
    className="flex h-4 w-4 shrink-0 items-center justify-center self-center rounded-full border border-primary transition-colors"
  >
    {selected && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
  </span>
)

// A build option's content: title with any badge beside it, description
// under both, and the radio pinned to the far right. The container that owns the
// card border lives in StepBuild, so a build option with a framework follow-up
// can share one continuous block with it. The card is WHITE in every state — the
// border and the filled radio carry selection; an earlier tinted treatment was
// dropped because it made the card compete with its own nested question.
//
// The badge sits WITH the title rather than anchored to the card's right edge:
// right-anchored parks it a long way from the words it qualifies, reading as
// loose furniture rather than a label on the option. The radio owns the right
// edge alone, so the controls still line up.
export const BuildCard = ({
  option,
  selected,
  expanded,
  onSelect,
}: {
  option: BuildOption
  selected: boolean
  // A follow-up panel renders directly below, so bottom padding drops from p-4 to
  // pb-2. It stacks with the panel's own mt-2 for a 16px gap above the grey — a
  // little more than the 8px it sits inset from the card's other three edges,
  // deliberately: the description above it is prose, and butting a filled panel
  // straight up against it read as cramped.
  expanded: boolean
  onSelect: (value: BuildChoice) => void
}) => {
  // The badge's tooltip opens on hover of a non-focusable <span> inside this
  // button, so keyboard and screen-reader users focusing the card never reach
  // it. The same text is therefore exposed as the button's accessible
  // DESCRIPTION: aria-describedby points at a visually-hidden sibling rendered
  // OUTSIDE the button, so it is announced with the card but is not folded into
  // the button's accessible name (which would read the sentence twice). Mouse
  // users still get the hover tooltip; nothing visual changes.
  const descriptionId = option.badge?.tooltip
    ? `onboarding-build-${option.value}-badge-note`
    : undefined
  return (
    <>
      <button
        type="button"
        onClick={() => onSelect(option.value)}
        // aria-pressed, not role="radio": clicking the selected option clears it (see
        // StepBuild), and a radio has no such state to announce. A toggle button does.
        aria-pressed={selected}
        // Also a disclosure: selecting can put a follow-up question on screen below,
        // and aria-expanded is the only signal a screen reader gets that it did.
        aria-expanded={expanded}
        aria-describedby={descriptionId}
        data-testid={`onboarding-build-${option.value}`}
        // White in every state: the border and the filled radio carry selection, and
        // the grey belongs to the follow-up panel (see StepBuild).
        className={cn(
          'flex w-full items-start gap-3 px-4 pt-4 text-left',
          expanded ? 'pb-2' : 'pb-4',
        )}
      >
        {/* gap-1, not the gap-0.5 this used before the badge moved in: a 24px title
        row over a 2px gap read as one crowded block. */}
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          {/* min-h-5 (20px) is both the p-600 title's line box and the badge's height,
          pinned so the title row measures the same with or without one — otherwise
          the description sits at a different y on badged and unbadged cards, which
          is visible the moment they are stacked in a list. flex-wrap so a narrow
          card drops the badge to its own line instead of squeezing the title. */}
          <span className="flex min-h-5 flex-wrap items-center gap-2">
            <span className="text-p-600 text-foreground">{option.title}</span>
            {option.badge && <OptionBadge {...option.badge} />}
          </span>
          <span className="text-s-400 text-secondary">{option.description}</span>
        </span>
        <Radio selected={selected} />
      </button>
      {option.badge?.tooltip && (
        <span id={descriptionId} className="sr-only">
          {option.badge.tooltip}
        </span>
      )}
    </>
  )
}

// A framework option: a bordered radio pill in a wrapping row (main's
// shipped design, unchanged — only the option LISTS differ).
// A toggle-shaped button rather than a native radio input, matching the build
// cards above; selecting paints the primary border, a light primary wash and
// the filled radio.
export const FrameworkPill = ({
  option,
  selected,
  onSelect,
}: {
  option: FrameworkOption
  selected: boolean
  onSelect: (value: Framework) => void
}) => (
  <button
    type="button"
    onClick={() => onSelect(option.value)}
    // aria-pressed, matching the build cards above. Without it the pill's
    // selected state lives ONLY in the border colour and the painted glyph —
    // both invisible to a screen reader, which would announce every option
    // identically and give no way to tell which one is chosen. Not role="radio":
    // these are buttons in a plain container, not a composite radiogroup with
    // arrow-key navigation, and claiming that role would promise keyboard
    // behaviour the widget does not implement.
    aria-pressed={selected}
    data-testid={`onboarding-framework-${option.value}`}
    // bg-background so the pills stay white on the panel's tinted surface — with
    // the probe now in a grey block (see StepFramework) a transparent unselected
    // pill dissolved into it, leaving only a hairline.
    className={cn(
      // `group` so the label can darken on hover of the whole pill, not just of
      // the text itself.
      'group flex w-fit items-center gap-2 rounded-lg border bg-background px-3 py-2 text-left transition-colors',
      selected ? 'border-primary' : 'cursor-pointer border-st2 hover:border-st1',
    )}
  >
    <Radio selected={selected} />
    {/* Grey at rest, black on HOVER and when selected — the design system's
        reach-for-it interaction (neo's segmented tabs do the same walk, and
        text-secondary → hover:text-foreground is the app's idiom for it). An
        unselected option is not an answer yet, so it should not read as black
        until you are either pointing at it or have picked it.
        text-secondary, NOT tertiary: tertiary looks greyer but measures 2.56:1
        on white, under the 4.5:1 AA floor for a control label. */}
    <span
      className={cn(
        'whitespace-nowrap text-s-500 transition-colors',
        selected ? 'text-foreground' : 'text-secondary group-hover:text-foreground',
      )}
    >
      {option.label}
    </span>
  </button>
)
